import { AxiosError } from 'axios';

import {
  ACTIVATION_LINK_INVALID_MESSAGE,
  ACTIVATION_LINK_USED_MESSAGE,
  ACTIVATION_UNEXPECTED_ERROR_MESSAGE,
} from './Activation.constants';
import {
  ActivationBlockReason,
  CompanyActivationTokenStatus,
  CompanyActivationTokenValidateResponse,
} from './Activation.types';

type ActivationErrorResponse = {
  message?: string;
  reason?: string;
  status?: string;
};

function hasUsedTokenText(text?: string) {
  const normalizedText = text?.toLowerCase() || '';

  return (
    normalizedText.includes('used') ||
    normalizedText.includes('utiliz') ||
    normalizedText.includes('already') ||
    normalizedText.includes('já')
  );
}

export function getActivationErrorMessage(error: unknown) {
  const axiosError = error as AxiosError<ActivationErrorResponse>;
  const data = axiosError.response?.data;

  return `${
    data?.reason || data?.message || data?.status || axiosError.message || ''
  }`;
}

export function isActivationTokenUsed(
  token?: CompanyActivationTokenValidateResponse,
) {
  return token?.status === CompanyActivationTokenStatus.Used;
}

export function isActivationTokenAvailable(
  token?: CompanyActivationTokenValidateResponse,
) {
  return (
    !!token?.isValid && token.status === CompanyActivationTokenStatus.Pending
  );
}

export function getActivationBlockReason(error: unknown) {
  const axiosError = error as AxiosError<ActivationErrorResponse>;
  const httpStatus = axiosError.response?.status;
  const tokenStatus = axiosError.response?.data?.status;
  const errorText = getActivationErrorMessage(error);

  if (
    httpStatus === 404 ||
    tokenStatus === CompanyActivationTokenStatus.Cancelled
  ) {
    return ActivationBlockReason.Invalid;
  }

  if (
    tokenStatus === CompanyActivationTokenStatus.Used ||
    hasUsedTokenText(errorText)
  ) {
    return ActivationBlockReason.Used;
  }

  return ActivationBlockReason.Unexpected;
}

export function getActivationTokenBlockReason(
  token: CompanyActivationTokenValidateResponse,
) {
  if (isActivationTokenUsed(token)) {
    return ActivationBlockReason.Used;
  }

  return ActivationBlockReason.Invalid;
}

export function getActivationBlockMessage(reason: ActivationBlockReason) {
  if (reason === ActivationBlockReason.Used) {
    return ACTIVATION_LINK_USED_MESSAGE;
  }

  if (reason === ActivationBlockReason.Unexpected) {
    return ACTIVATION_UNEXPECTED_ERROR_MESSAGE;
  }

  return ACTIVATION_LINK_INVALID_MESSAGE;
}

export function getActivationWorkshopName(
  token?: CompanyActivationTokenValidateResponse,
) {
  const client = token?.billingRequest?.client;

  return client?.fantasyName || client?.companyName || client?.name;
}

export function getActivationWorkshopEmail(
  token?: CompanyActivationTokenValidateResponse,
) {
  const client = token?.billingRequest?.client;

  return client?.email || client?.financialEmail;
}
